import {
  Box,
  TextField,
  InputLabel,
  ButtonGroup,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { styled } from "@mui/material/styles";

const StyledButtonGroup = styled(ButtonGroup)({
  "& .MuiButtonGroup-grouped": {
    borderRadius: "5px",
    border: "1px solid #d0d5dd",
    marginBottom: "15px",
    height: "45px",
    transition: "background-color 0.3s",
    "&:hover": {
      border: "1px solid #d0d5dd",
    },
  },
});

type RangeOption = {
  label: string;
  min: number | string | undefined;
  max: number | string | undefined;
};

type PropsType = {
  title?: string;
  value?: string;
  onSelect: (value: string) => void;
  rangeOptions?: RangeOption[];
  defaultLabel?: string;
};

const NumberRangeSelector = ({
  title = "Page Likes",
  value,
  onSelect,
  rangeOptions,
  defaultLabel = "All",
}: PropsType) => {
  const theme = useTheme();
  const [minRange, setMinRange] = useState<string | number | undefined>();
  const [maxRange, setMaxRange] = useState<string | number | undefined>();
  const [prevValue, setPrevValue] = useState(value);

  // Sync state from prop during render (not in useEffect) to avoid extra render cycle
  if (value !== prevValue) {
    setPrevValue(value);
    if (value) {
      const [minValue, maxValue] = value.split("__").map(Number);
      setMinRange(minValue);
      setMaxRange(maxValue);
    }
  }

  const handleRangeSelector = async (
    minRange: number | string | undefined,
    maxRange: number | string | undefined,
  ) => {
    // Ensure min and max ranges are valid numbers
    const min = minRange === "" || minRange === undefined || isNaN(Number(minRange)) ? 0 : Number(minRange);
    const max = maxRange === "" || maxRange === undefined || isNaN(Number(maxRange)) ? 
      (rangeOptions && rangeOptions.length > 0 ? 
        Number(rangeOptions[rangeOptions.length-1].max) : 100000) : Number(maxRange);
    
    setMinRange(min);
    setMaxRange(max);
    onSelect(`${min}__${max}`);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        p: 2,
        borderRadius: 1,
      }}
    >
      <InputLabel id="media-type-select" sx={{ mb: 2, fontWeight: "bold" }}>
        {title}
      </InputLabel>
      {rangeOptions && (
        <StyledButtonGroup orientation="vertical" fullWidth>
          <Button
            variant={!maxRange && !minRange ? "contained" : "outlined"}
            onClick={() => {
              // Use default min/max values when the user selects "All"
              const defaultMin = 0;
              const defaultMax = rangeOptions && rangeOptions.length > 0 ? 
                Number(rangeOptions[rangeOptions.length-1].max) : 100000;
              
              setMinRange(defaultMin);
              setMaxRange(defaultMax);
              onSelect(`${defaultMin}__${defaultMax}`);
            }}
          >
            {defaultLabel}
          </Button>
        </StyledButtonGroup>
      )}
      <StyledButtonGroup orientation="vertical" fullWidth>
        {rangeOptions &&
          rangeOptions.map((option, index) => (
            <Button
              key={index}
              onClick={() => handleRangeSelector(option.min, option.max)}
              variant={
                minRange === option.min && maxRange === option.max
                  ? "contained"
                  : "outlined"
              }
            >
              {option.label}
            </Button>
          ))}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ marginRight: "auto" }}>
            <TextField
              type="number"
              placeholder="Min"
              variant="outlined"
              value={minRange}
              onChange={(e) => handleRangeSelector(e.target.value, maxRange)}
            />
          </Box>
          <Typography>-</Typography>
          <Box sx={{ marginLeft: "auto" }}>
            <TextField
              type="number"
              placeholder="Max"
              variant="outlined"
              value={maxRange}
              onChange={(e) => handleRangeSelector(minRange, e.target.value)}
            />
          </Box>
        </Box>
      </StyledButtonGroup>
    </Box>
  );
};

export default NumberRangeSelector;
